import { createSlice, createAsyncThunk, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import apiClient from '@/lib/apiClient';

export interface UserState {
  user: { id?: string; name?: string; email?: string; type?: 'agency' | 'individual' | 'admin'; status?: 'active' | 'pending' } | null;
  websites: string[];
  websiteItems: Array<{ id?: string; name: string; status?: string }>;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  lastFetched: number | null; // timestamp to allow cache invalidation TTL if needed later
  errorCode?: number | null; // HTTP status code from last failure (e.g., 401/403/404)
}

interface DomainObj { id?: string; name?: string; status?: string }
export interface MeResponse {
  id?: string;
  name?: string;
  email?: string;
  type?: 'agency' | 'individual' | 'admin';
  status?: 'active' | 'pending';
  domains?: Array<string | DomainObj>;
}

const initialState: UserState = {
  user: null,
  websites: [],
  websiteItems: [],
  status: 'idle',
  error: null,
  lastFetched: null,
  errorCode: null,
};

// Fetch /auth/me. supports force refresh.
export const fetchMe = createAsyncThunk<
  MeResponse,
  { force?: boolean } | void,
  { state: { user: UserState }; rejectValue: { message: string; code?: number } }
>(
  'user/fetchMe',
  async (arg, { rejectWithValue }) => {
    try {
      const headers = { 'X-Skip-Auth-Redirect': '1', 'Cache-Control': 'no-cache' };
      const res = await apiClient.get<MeResponse>('/auth/me', { headers });
      return res.data;
    } catch (err: unknown) {
      if (typeof err === 'object' && err && 'response' in err) {
        // @ts-expect-error dynamic access
        const status = err.response?.status as number | undefined;
        // @ts-expect-error dynamic access
        const msg = err.response?.data?.message as string | undefined;
        return rejectWithValue({ message: typeof msg === 'string' ? msg : 'Failed to load user', code: status });
      }
      return rejectWithValue({ message: 'Failed to load user' });
    }
  },
  {
    condition: (arg, { getState }) => {
      const state = getState();
      const { status, lastFetched } = state.user;
      const force = typeof arg === 'object' && arg ? arg.force : false;
      
      if (status === 'loading') return false; // avoid duplicate in-flight
      if (!force && lastFetched) return false; // cached if not forced
      return true;
    },
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    logoutCleared(state: UserState) {
      state.user = null;
      state.websites = [];
      state.websiteItems = [];
      state.status = 'idle';
      state.error = null;
      state.lastFetched = null;
    },
    clearUserCache(state: UserState) {
      // Clear cache to force refetch
      state.lastFetched = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<UserState>) => {
    builder
      .addCase(fetchMe.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.errorCode = null;
      })
      .addCase(fetchMe.fulfilled, (state, action: PayloadAction<MeResponse>) => {
        state.status = 'succeeded';
        const data = action.payload || {};
        const type = data.type;
        const status = data.status;
        state.user = { id: data.id, name: data.name, email: data.email, type, status };
        const rawWebsites: Array<string | DomainObj> = Array.isArray(data.domains) ? data.domains : [];
  const items: Array<{ id?: string; name: string; status?: string }> = [];
        const list: string[] = [];
        for (const w of rawWebsites) {
          const url: string | undefined = typeof w === 'string' ? w : w.name;
          if (typeof url === 'string' && url.length > 0) {
            list.push(url);
            const id = typeof w === 'object' ? w.id : undefined;
            const status = typeof w === 'object' ? w.status : undefined;
            items.push({ id, name: url, status });
          }
        }
        state.websites = list;
        state.websiteItems = items;
        state.lastFetched = Date.now();
        state.error = null;
        state.errorCode = null;
      })
      .addCase(fetchMe.rejected, (state, action) => {
        state.status = 'failed';
        const payload = action.payload as { message?: string; code?: number } | undefined;
        state.error = payload?.message || 'Unknown error';
        state.errorCode = typeof payload?.code === 'number' ? payload!.code! : null;
      });
  },
});

export const { logoutCleared, clearUserCache } = userSlice.actions;
export default userSlice.reducer;
